import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function PetTurfPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-sky-600 text-white text-xs font-bold px-4 py-2 rounded-md mb-6 uppercase tracking-wide">Pet Turf</span>
              <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">The Perfect Lawn For Your Pets</h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Durable, safe, and easy-to-clean synthetic turf designed specifically for dogs and pets. Say goodbye to muddy paws, brown spots, and constant yard maintenance in Tulsa.
              </p>
              <div className="flex flex-wrap gap-5">
                <a href="tel:918-806-8873" className="bg-sky-600 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-sky-700 transition-all hover:scale-105 whitespace-nowrap">
                  Get A Quote
                </a>
                <Link to="/about" className="bg-transparent border-2 border-sky-600 text-sky-600 px-8 py-4 rounded-full text-base font-semibold hover:bg-sky-600 hover:text-white transition-all whitespace-nowrap">
                  Learn More
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/ba55428b6f60abc25ac49fb0d9328676.jpg" 
                alt="Pet Turf Tulsa Oklahoma"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Why Pet Owners Love Our Turf</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Engineered specifically for pets with features that make life easier for you and safer for them</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-drop-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Superior Drainage</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Advanced drainage system removes pet urine quickly and efficiently—up to 30 inches per hour. No odors, no puddles, no mess. Keeps your Tulsa yard fresh and clean.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-shield-check-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Safe &amp; Non-Toxic</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Made in USA with pet-safe, non-toxic materials. No harmful chemicals, lead-free, and antimicrobial properties help prevent bacteria growth. Safe for paws and noses.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-sparkling-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Easy To Clean</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Simple maintenance with just water and occasional pet-safe cleaner. No more digging up dead grass or dealing with muddy paws. Hose it down and you're done.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-footprint-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">No More Muddy Paws</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Keep your Tulsa home clean with a yard that doesn't turn into mud after rain. No dirt tracked inside, no stained carpets, no constant cleaning. Just clean paws every time.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-plant-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">No Brown Spots</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Pet urine won't kill your grass anymore. No more unsightly brown patches or constant reseeding. Your yard stays uniformly green and beautiful year-round.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-7">
                <i className="ri-hammer-line text-6xl text-sky-600"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Dig-Resistant</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Durable backing and proper installation prevent digging. Even the most determined diggers will find it difficult to damage properly installed pet turf.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Engineered For Pets</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-sky-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Antimicrobial Protection</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Built-in antimicrobial technology inhibits bacteria growth and helps control odors. Keeps your pet area hygienic and fresh-smelling even with heavy use.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-sky-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Maximum Drainage Rate</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Perforated backing allows liquids to drain through immediately. Water and urine flow through at 30+ inches per hour—faster than natural grass can absorb.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-sky-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Durable Fibers</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Heavy-duty polyethylene fibers resist wear from running, playing, and digging. Designed to withstand the toughest pets while maintaining soft comfort.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-sky-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Temperature Control</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Stays cooler than standard turf in Oklahoma's hot summers. Special fibers and infill options help regulate surface temperature for comfortable paws.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-3xl text-sky-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Realistic Look &amp; Feel</h3>
                    <p className="text-base text-gray-600 leading-relaxed">
                      Soft, natural-looking grass that pets love. Multi-toned blades create authentic appearance while providing comfortable surface for playing and lounging.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=close-up%20of%20pet-friendly%20artificial%20synthetic%20turf%20grass%20with%20drainage%20holes%20visible%2C%20durable%20fake%20grass%20for%20dogs%2C%20realistic%20green%20pet%20turf%20texture%2C%20professional%20quality%20material%2C%20clean%20simple%20background&width=800&height=900&seq=petdetail918&orientation=portrait" 
                alt="Pet Turf Features"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Simple Maintenance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Keep your pet turf fresh and clean with minimal effort</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-8 rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center bg-sky-600 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Daily Cleanup</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Remove solid waste just like you would from natural grass. The turf surface makes cleanup quick and easy with no mess left behind.
              </p>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-8 rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center bg-sky-600 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Weekly Rinse</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Hose down the turf once a week to rinse away any residue. The superior drainage ensures water flows through quickly without pooling.
              </p>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-8 rounded-2xl">
              <div className="w-16 h-16 flex items-center justify-center bg-sky-600 rounded-full mb-6">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monthly Deep Clean</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Use a pet-safe turf cleaner once a month for deep cleaning. This keeps the turf fresh, controls odors, and maintains antimicrobial protection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Perfect For Every Pet Space</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From small dog runs to large backyards in Tulsa</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-product-shop>
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=residential%20backyard%20with%20pet-friendly%20artificial%20turf%20for%20dogs%20in%20Tulsa%20Oklahoma%2C%20fenced%20yard%20with%20synthetic%20grass%2C%20happy%20pets%20playing%2C%20clean%20green%20lawn%2C%20simple%20background&width=600&height=600&seq=backyard918pet&orientation=squarish" 
                  alt="Backyard Pet Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Backyards</h3>
                <p className="text-sm text-gray-600">Full yard installations for Tulsa homes with pets</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=dog%20run%20with%20artificial%20synthetic%20pet%20turf%20in%20Tulsa%20Oklahoma%2C%20narrow%20side%20yard%20with%20fake%20grass%20for%20dogs%2C%20fenced%20pet%20area%2C%20durable%20turf%20installation%2C%20simple%20background&width=600&height=600&seq=dogrun918&orientation=squarish" 
                  alt="Dog Run Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dog Runs</h3>
                <p className="text-sm text-gray-600">Side yards and dedicated pet areas</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=commercial%20dog%20daycare%20facility%20with%20artificial%20pet%20turf%20in%20Tulsa%20Oklahoma%2C%20professional%20pet%20care%20center%20with%20synthetic%20grass%2C%20multiple%20dogs%20playing%2C%20clean%20safe%20environment%2C%20simple%20background&width=600&height=600&seq=daycare918&orientation=squarish" 
                  alt="Dog Daycare Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dog Daycares</h3>
                <p className="text-sm text-gray-600">Commercial pet facilities and boarding kennels</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <div className="relative h-64 w-full">
                <img 
                  src="https://readdy.ai/api/search-image?query=apartment%20balcony%20with%20artificial%20pet%20turf%20for%20small%20dogs%20in%20Tulsa%20Oklahoma%2C%20compact%20synthetic%20grass%20area%2C%20urban%20pet%20space%2C%20potty%20area%20for%20dogs%2C%20simple%20background&width=600&height=600&seq=balcony918pet&orientation=squarish" 
                  alt="Balcony Pet Turf"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Balconies &amp; Patios</h3>
                <p className="text-sm text-gray-600">Small spaces and apartment pet areas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-sky-600 to-blue-700">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">Give Your Pets The Perfect Yard</h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Get a free quote on pet-friendly turf for your Tulsa property. Our team will help you create a safe, clean, and beautiful space your pets will love.
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            <a href="tel:918-806-8873" className="bg-white text-sky-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap">
              Call 918-806-8873
            </a>
            <Link to="/about" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white hover:text-sky-600 transition-all whitespace-nowrap">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}